// Foyers: every home on the left, nearest to empty first; the chosen home on
// the right with the app's big cylinder, 60 days of levels, its devices and
// its orders.
import { useMemo, useState } from 'react';
import { S } from '../lib/strings';
import { dayLabel, dec1, hhmm, minutesBetween, rel } from '../lib/format';
import { prettyPhone, telHref, waHref } from '../lib/phone';
import { isOpen } from '../lib/orders';
import type { Source } from '../lib/api';
import type { HomeDetail, HomeRow } from '../lib/types';
import { BigBottle, BrandCyl, MiniBottle } from '../components/Bottles';
import { Flame, Ic } from '../components/Icon';
import { LevelChart } from '../components/LevelChart';
import { Bat, Sig } from '../components/Device';
import type { Tab } from '../components/Shell';
import { StPill } from './Orders';
import { useRemind } from '../components/OrderActions';
import { Loading, SkDetail } from '../components/Skeleton';
import { ErrorState } from '../components/Busy';
import { useResource } from '../lib/useResource';

type HF = 'all' | 'low' | 'run' | 'dev';
const lvl = (h: HomeRow) => h.home?.level ?? null;
const silentH = (h: HomeRow) => (h.home?.received_at ? Math.round(minutesBetween(h.home.received_at) / 60) : null);
const daysLeft = (h: HomeRow) => (h.pace ? h.pace.days_left : null);
const match = (h: HomeRow, f: HF) =>
  f === 'all' || (f === 'low' && h.connected && (lvl(h) ?? 100) < 20) || (f === 'run' && !!h.open_order) ||
  (f === 'dev' && h.connected && !h.home?.online);
const rank = (h: HomeRow) => (h.connected ? daysLeft(h) ?? (lvl(h) ?? 100) / 5 : 999);

interface Props { src: Source; refresh: Date | null; selected: string | null; onSelect(id: string): void; openOrder(id: string): void; go(t: Tab): void; openGz(q?: string): void }

export function Homes({ src, refresh, selected, onSelect, openOrder, go, openGz }: Props) {
  const [f, setF] = useState<HF>('all');
  const [q, setQ] = useState('');
  const [tick, setTick] = useState(0);
  const remind = useRemind(src, () => setTick((t) => t + 1));

  const all = useResource<HomeRow[]>(() => src.homes(), [src], { refresh: [refresh] });
  const homes = all.data;
  const list = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return (homes ?? []).filter((h) => match(h, f) && (!qq || (h.name + ' ' + h.quartier + ' ' + (h.phone ?? '')).toLowerCase().includes(qq)))
      .sort((a, b) => rank(a) - rank(b));
  }, [homes, f, q]);
  const sel = selected ?? list[0]?.customer_id ?? null;
  // Another home drops the one on screen at once: its outline shows, never the previous home under the new name.
  const one = useResource<HomeDetail>(sel ? () => src.home(sel) : null, [src, sel], { refresh: [refresh, tick] });

  if (all.status === 'error') return <ErrorState error={all.error!} onRetry={all.reload} retrying={all.retrying} />;
  if (!homes) return <Loading page="hh" />;
  const connected = homes.filter((h) => h.connected).length;

  return (
    <section className="view on" aria-label={S.hh.title}>
      <div className="ph"><div><h1>{S.hh.title}</h1><p>{S.hh.sub(connected)}</p></div></div>
      <div className="md">
        <div className="card hlist">
          <div className="hh">
            <label className="search"><Ic n="search" s="xs" /><span className="sr">{S.hh.find}</span><input placeholder={S.hh.findPh} value={q} onChange={(e) => setQ(e.target.value)} /></label>
            <div className="fl">
              {(Object.keys(S.hh.filters) as HF[]).map((k) => (
                <button key={k} className="fchip" aria-pressed={f === k} onClick={() => setF(k)}>{S.hh.filters[k]}<span className="c">{homes.filter((h) => match(h, k)).length}</span></button>
              ))}
            </div>
          </div>
          <div className="hrows">
            {list.length ? list.map((h) => <HomeLine key={h.customer_id} h={h} sel={h.customer_id === sel} onClick={() => onSelect(h.customer_id)} />)
              : <div className="empty"><b>{S.hh.none}</b>{S.hh.noneSub}</div>}
          </div>
        </div>
        <div className="hd">
          {!sel ? <div className="card empty"><b>{S.hh.pick}</b></div>
            : one.data ? <HomeCard h={one.data} openOrder={openOrder} go={go} openGz={openGz} remind={remind} />
              : one.status === 'error' ? <ErrorState inline error={one.error!} onRetry={one.reload} retrying={one.retrying} /> : <SkDetail />}
        </div>
      </div>
    </section>
  );
}

function HomeLine({ h, sel, onClick }: { h: HomeRow; sel: boolean; onClick(): void }) {
  const l = lvl(h), d = daysLeft(h), s = silentH(h);
  return (
    <button className={'hr' + (sel ? ' sel' : '')} onClick={onClick}>
      {h.connected ? <MiniBottle level={l ?? 0} w={30} /> : <span style={{ width: 30, display: 'grid', placeItems: 'center', color: 'var(--ink-4)' }}><Ic n="tool" s="s" /></span>}
      <span style={{ minWidth: 0 }}>
        <b>{h.name}</b>
        <span className="sub">{h.marketer && <span className="bdot" style={{ background: h.marketer.brand_color }} />}{h.marketer ? h.marketer.name + ' · ' : ''}{h.quartier}</span>
      </span>
      <span className="lv">
        {h.connected && l != null ? <>
          <b className={'num' + (l < 20 ? ' lowtxt' : '')}>{l} %</b>
          <span className={!h.home?.online || (d != null && d < 3) ? 'lowtxt' : 'muted'}>{!h.home?.online && s != null ? S.hh.silent(s) : d != null ? S.hh.empty(d) : ''}</span>
        </> : <span className="muted">{h.failed ? S.hh.reinstall : h.state === 'waitlist' ? S.hh.waitlist : h.state === 'awaiting_install' ? S.hh.installing : S.hh.noKit}</span>}
      </span>
    </button>
  );
}

function HomeCard({ h, openOrder, go, openGz, remind }: { h: HomeDetail; openOrder(id: string): void; go(t: Tab): void; openGz(q?: string): void; remind(ids: string[]): void }) {
  const l = lvl(h), d = daysLeft(h), open = h.order_list.find((o) => isOpen(o.status));
  const emptyAt = d != null ? dayLabel(new Date(Date.now() + d * 86_400_000), { weekday: 'short', day: 'numeric', month: 'short' }) : '—';
  const kit = h.kit;
  return (
    <>
      <div className="card hhero">
        <div className="bigb"><BigBottle level={h.connected ? l ?? 0 : 0} w={150} />{h.connected && l != null && <div className={'pct num' + (l < 45 ? ' dk' : '')}>{l} %</div>}</div>
        <div className="hinfo">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            {open ? <StPill o={open} /> : h.connected && l != null && l < 20 ? <span className="st red">{S.hh.below}</span> : null}
            {h.connected && !h.home?.online && <span className="st red">{S.hh.silentPill}</span>}
          </div>
          <h2>{h.name}</h2>
          <div className="ad"><Ic n="pin" s="xs" />{h.quartier}, {S.hh.city}<span>·</span>{S.hh.since(dayLabel(h.since, { day: 'numeric', month: 'long' }))}</div>
          <div className="facts">
            <div className="fact"><span className="cap">{S.hh.level}</span><b className={'num' + (l != null && l < 20 ? ' lowtxt' : '')}>{l != null ? l + ' %' : '—'}</b><span>{l != null ? S.hh.left(dec1((l / 100) * h.size_kg)) : S.hh.noMeasure}</span></div>
            <div className="fact"><span className="cap">{S.hh.emptyAt}</span><b>{emptyAt}</b><span>{d != null ? S.hh.empty(d) : ''}</span></div>
            <div className="fact"><span className="cap">{S.hh.use}</span><b className="num">{h.pace ? S.hh.perDay(dec1(h.pace.kg_per_day)) : '—'}</b><span>{S.hh.avg}</span></div>
            <div className="fact"><span className="cap">{S.hh.bottle}</span><b style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{h.marketer && <BrandCyl color={h.marketer.brand_color} h={22} />}{h.marketer?.name ?? S.hh.unknownBrand}</b><span>{dec1(h.size_kg)} kg</span></div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {open && <button className="btn pri" onClick={() => openOrder(open.id)}>{S.hh.follow}</button>}
            {!open && h.connected && l != null && l < 50 && <button className="btn pri" onClick={() => remind([h.customer_id])}>{S.act.remind}</button>}
            {h.phone && <a className="btn" href={telHref(h.phone)}><Ic n="phone" s="s" />{prettyPhone(telHref(h.phone).slice(4))}</a>}
            {h.phone && <a className="btn" href={waHref(h.phone)} target="_blank" rel="noreferrer" title={S.waHint}><Ic n="msg" s="s" />{S.wa}</a>}
            <button className="btn" onClick={() => openGz(S.gz.aboutHome(h.name))}><Flame />{S.hh.ask}</button>
          </div>
        </div>
      </div>
      {h.connected && h.days60 && l != null && (
        <div className="card">
          <div className="ch"><div><h2>{S.hh.chart}</h2><div className="sub">{S.hh.chartSub}</div></div></div>
          <div className="cb"><LevelChart days={h.days60} level={l} daysLeft={d} /></div>
        </div>
      )}
      <div className="card pad">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{S.hh.devices(h.name)}</h2>
          <button className="link" onClick={() => go('dev')}>{S.hh.allDevices} <Ic n="arrow" s="xs" /></button>
        </div>
        <div className="dev2">
          <div className="devc"><span className="di"><Ic n="cyl" s="s" /></span><div><b>{S.hh.sensor}</b><span className="mono">{kit?.sensor.serial ?? '—'}</span></div>
            <div className="r">{kit ? <><Bat pct={kit.sensor.battery_pct} /><br /><span>{S.hh.measured(kit.measured_at ? rel(minutesBetween(kit.measured_at)) : '—')}</span></> : <span className="st grey">{S.hh.toFit}</span>}</div></div>
          <div className="devc"><span className="di"><Ic n="wifi" s="s" /></span><div><b>{S.hh.gateway}</b><span className="mono">{kit ? kit.gateway.serial + (kit.gateway.fw ? ' · v' + kit.gateway.fw : '') : '—'}</span></div>
            <div className="r">{kit ? <><Sig rssi={kit.gateway.rssi} online={kit.online} /><br /><span className="num">{!kit.online ? S.hh.offline : kit.gateway.rssi != null ? kit.gateway.rssi + ' dBm' : '—'}</span></> : <span className="st grey">{S.hh.toFit}</span>}</div></div>
        </div>
      </div>
      <div className="card pad olist">
        <h2 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 600 }}>{S.hh.orders}</h2>
        {h.order_list.length ? h.order_list.map((o) => (
          <div className="orow" key={o.id} onClick={() => openOrder(o.id)}>
            <BrandCyl color={o.marketer?.brand_color ?? '#94A3B8'} h={28} />
            <div><b style={{ fontSize: 13.5 }}>{o.order_type === 'first_install' ? S.hh.install : S.hh.refillOf(o.marketer?.name ?? '')}</b>
              <div className="muted mono">{o.ref} · {dayLabel(o.created_at)} {hhmm(o.created_at)}</div></div>
            <StPill o={o} /><Ic n="chev" s="s" />
          </div>
        )) : <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>{S.hh.noOrders}</div>}
        {h.orders > 0 && <div className="orow" style={{ cursor: 'default' }}><span /><div className="muted" style={{ fontSize: 12.5 }}>{S.hh.before(h.orders)}</div><span /><span /></div>}
      </div>
    </>
  );
}
